import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';

// Material UI v4
import { makeStyles } from '@material-ui/core/styles';
import {
  Grid, Button, IconButton, Typography, Tabs, Tab,
  AppBar, Snackbar, Box, Hidden
} from '@material-ui/core';

// Iconos MUI v4
import CloseIcon from '@material-ui/icons/Close';
import ImageIcon from '@material-ui/icons/Image';
import PersonPinIcon from '@material-ui/icons/PersonPin';
import HelpIcon from '@material-ui/icons/Help';
import FormatColorFill from '@material-ui/icons/FormatColorFill';
import PhotoCamera from '@material-ui/icons/PhotoCamera';

// Librerías
import { ChromePicker } from 'react-color';

// Recursos Propios
import Utils from '../resources/Utils';
import {
  checkAccount,
  requestEditOfficeConfigs,
  requestEditConfigsColors,
  requestEditConfigMainLogo
} from '../actions/actions';

// ----------------------------------------------------------------------
// Estilos con JSS (MUI v4)
// ----------------------------------------------------------------------
const useStyles = makeStyles((theme) => ({
  cover: {
    position: 'fixed',
    top: 0, right: 0, bottom: 0, left: 0,
    zIndex: 2
  },
  colorPickerContainer: {
    position: 'absolute',
    zIndex: 1300,
    marginTop: '10px'
  },
  buttons: {
    height: '35px',
    width: '100%',
    borderRadius: '8px',
    border: '1px solid #ccc',
    cursor: 'pointer',
    boxShadow: '0px 2px 4px rgba(0,0,0,0.1)'
  },
  
  // 🔥 NUEVOS ESTILOS PARA FLEXBOX 🔥
  mainWrapper: {
    // Al quitar el height fijo, el contenedor se adapta al tamaño de su contenido
    display: 'flex',
    flexDirection: 'column',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    overflow: 'hidden'
  },
  scrollableContent: {
    // Ya no forzamos el overflow automático ni el flex expansivo
    backgroundColor: '#ffffff',
    paddingBottom: '20px' // Un poco de aire extra al final para que no quede pegado el botón
  },
  // ---------------------------------
  
  imageUploadBox: {
    marginTop: 16,
    width: 200,
    height: 200,
    background: '#f5f5f5',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#888',
    borderRadius: 8,
    border: '2px dashed #ccc',
    cursor: 'pointer',
    transition: 'background 0.3s ease',
    '&:hover': {
      background: '#eeeeee'
    }
  }
}));

function extraerDesdeDerecha(texto, caracter) {
    // Validaciones de entrada
    if (typeof texto !== "string" || typeof caracter !== "string" || caracter.length !== 1) {
        throw new Error("Parámetros inválidos: se espera (string, carácter único).");
    }

    // Buscar la última aparición del carácter
    const indice = texto.lastIndexOf(caracter);

    // Si no se encuentra, devolver cadena vacía
    if (indice === -1) {
        return "";
    }

    // Extraer desde el carácter encontrado hasta el final
    return texto.slice(indice + 1);
}

// ----------------------------------------------------------------------
// Componentes Auxiliares
// ----------------------------------------------------------------------
function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box p={3}>
          <Typography component="div">{children}</Typography>
        </Box>
      )}
    </div>
  );
}
TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired,
};

function a11yProps(index) {
  return {
    id: `full-width-tab-${index}`,
    'aria-controls': `full-width-tabpanel-${index}`,
  };
}

// ----------------------------------------------------------------------
// Componente Principal
// ----------------------------------------------------------------------
const ConfigsTable = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();

  const { init } = useSelector((state) => state);
  const configData = init.config?.data || {};

  const [valueTabs, setValueTabs] = useState(0);

  const [colors, setColors] = useState({
    appBarColor: configData.appBarColor || '',
    primaryColor: configData.primaryColor || '',
    secondaryColor: configData.secondaryColor || '',
    textDescriptionColor: configData.textDescriptionColor || '',
    textTitleColor: configData.textTitleColor || ''
  });

  const [activePickers, setActivePickers] = useState({});

  const [image, setImage] = useState({
    selectedBase64: null,
    previewUrl: configData.mainLogo || null
  });

  const [snackbars, setSnackbars] = useState({
    general: false,
    iconsSuccess: false,
    iconsError: false,
    colorsSuccess: false,
    colorsError: false
  });

useEffect(() => {
    // Escucha para los colores
    const colorsStatus = init.updateColors?.status;
    if (colorsStatus === 'OK') {
      setSnackbars((prev) => ({ ...prev, colorsSuccess: true }));
      setTimeout(() => window.location.reload(), 1000);
    } else if (colorsStatus === 'ERROR') {
      setSnackbars((prev) => ({ ...prev, colorsError: true }));
    }

    // Escucha independiente para el Logo
    const logoStatus = init.updateMainLogo?.status;
    if (logoStatus === 'OK') {
      setSnackbars((prev) => ({ ...prev, iconsSuccess: true })); // O tu snackbar de éxito para imagen
      setTimeout(() => window.location.reload(), 1000);
    } else if (logoStatus === 'ERROR') {
      // Manejo de error para la imagen
      setSnackbars((prev) => ({ ...prev, iconsError: true })); 
    }
  }, [init.updateColors, init.updateMainLogo]);

  const handleChangeTabs = (event, newValue) => setValueTabs(newValue);

  const togglePicker = (key, isOpen) => {
    setActivePickers((prev) => ({ ...prev, [key]: isOpen }));
  };

  const handleColorChange = (key, hexValue) => {
    setColors((prev) => ({ ...prev, [key]: hexValue }));
  };

  const handleUpdateColors = () => {
    dispatch(requestEditConfigsColors({
      ...colors,
      officeId: configData.officeId
    }));
  };

const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // 750 KB expresado en bytes (750 * 1024)
      const MAX_FILE_SIZE_BYTES = 768000; 

      if (file.size > MAX_FILE_SIZE_BYTES) {
        alert("La imagen es demasiado pesada. El peso máximo permitido es de 750 KB.");
        e.target.value = null; // Limpia el input para permitir una nueva selección
        return; 
      }

      if (!file.type.startsWith('image/')) {
        alert("Por favor, selecciona un archivo de imagen válido (PNG, JPG, etc.).");
        e.target.value = null; 
        return;
      }

      // Si pasa los filtros, procesamos la imagen
      const reader = new FileReader();
      reader.onloadend = () => {
        setImage({
          selectedBase64: reader.result.split(',')[1],
          previewUrl: reader.result,
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveImage = () => {
    if (!image.selectedBase64) return;
    dispatch(requestEditConfigMainLogo({
      icon: image.selectedBase64,
      name: extraerDesdeDerecha(configData.mainLogo, '/') || null,
      officeId: configData.officeId
    }));
  };

  const closeSnackbar = (type) => {
    setSnackbars((prev) => ({ ...prev, [type]: false }));
  };

  const handleSubmit = (event) => event.preventDefault();

  const renderColorField = (label, key) => (
    <Grid item xs={12} sm={6} md={4}>
      <div style={{ marginBottom: '15px', position: 'relative' }}>
        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
          {label}
        </Typography>
        <button
          type="button"
          className={classes.buttons}
          onClick={() => togglePicker(key, true)}
          style={{ backgroundColor: colors[key] }}
        />
        {activePickers[key] && (
          <div>
            <div className={classes.cover} onClick={() => togglePicker(key, false)} />
            <div className={classes.colorPickerContainer}>
              <ChromePicker
                color={colors[key]}
                onChange={(e) => handleColorChange(key, e.hex)}
              />
            </div>
          </div>
        )}
      </div>
    </Grid>
  );

  const renderSnackbar = (message, open, type) => (
    <Snackbar
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      open={open}
      autoHideDuration={5000}
      onClose={() => closeSnackbar(type)}
      message={<span>{message}</span>}
      action={[
        <IconButton key="close" color="inherit" onClick={() => closeSnackbar(type)}>
          <CloseIcon />
        </IconButton>
      ]}
    />
  );

  const msgs = Utils.getMessages();

  return (
    <div>
      {renderSnackbar(msgs.configsmessages.updateSuccess, snackbars.general, 'general')}
      {renderSnackbar(msgs.configsmessages.updateColorsSuccess, snackbars.colorsSuccess, 'colorsSuccess')}
      {renderSnackbar(msgs.configsmessages.updateColorsError, snackbars.colorsError, 'colorsError')}
      {renderSnackbar(msgs.configsmessages.updateIconsSuccess, snackbars.iconsSuccess, 'iconsSuccess')}
      {renderSnackbar(msgs.configsmessages.updateIconsError, snackbars.iconsError, 'iconsError')}

      <Typography variant="h5" gutterBottom>
        {msgs.configsmessages.titleFormConfigs}
      </Typography>

      <form noValidate autoComplete="off" onSubmit={handleSubmit}>
        
        {/* 🔥 EL NUEVO CONTENEDOR FLEXBOX 🔥 */}
        <div className={classes.mainWrapper}>
          
          <AppBar position="static" style={{ backgroundColor: colors.appBarColor, boxShadow: 'none', borderBottom: '1px solid #ccc' }}>
            <Tabs
              value={valueTabs}
              onChange={handleChangeTabs}
              variant="fullWidth"
              scrollButtons="on"
              indicatorColor="primary"
              textColor="primary" 
            >
              <Tab 
                label={<Hidden smDown>{msgs.configsmessages?.configsColors || 'Colores'}</Hidden>} 
                icon={<FormatColorFill />} 
                {...a11yProps(0)} 
              />
              <Tab 
                label={<Hidden smDown>Iconos</Hidden>} 
                icon={<ImageIcon />} 
                {...a11yProps(1)} 
              />
              <Tab 
                label={<Hidden smDown>Próximamente</Hidden>} 
                icon={<PersonPinIcon />} 
                {...a11yProps(2)} 
              />
              <Tab 
                label={<Hidden smDown>Próximamente</Hidden>} 
                icon={<HelpIcon />} 
                {...a11yProps(3)} 
              />
            </Tabs>
          </AppBar>

          {/* ESTA ÁREA ES LA ÚNICA QUE HACE SCROLL */}
          <div className={classes.scrollableContent}>
            
            {/* TAB 0: COLORES */}
            <TabPanel value={valueTabs} index={0}>
              <Grid container spacing={3} style={{ padding: '10px 0' }}>
                {renderColorField(msgs.configs[32] || 'Primary Color', 'primaryColor')}
                {renderColorField(msgs.configs[33] || 'Secondary Color', 'secondaryColor')}
                {renderColorField(msgs.configs[0] || 'App Bar Color', 'appBarColor')}
                {renderColorField(msgs.configs[38] || 'Text Description', 'textDescriptionColor')}
                {renderColorField(msgs.configs[39] || 'Text Title', 'textTitleColor')}
              </Grid>

              <Box display="flex" justifyContent="center" mt={4} mb={2}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handleUpdateColors}
                  style={{ minWidth: '200px' }}
                >
                  {msgs.configsmessages.buttomUpdate}
                </Button>
              </Box>
            </TabPanel>

            {/* TAB 1: ICONOS */}
            <TabPanel value={valueTabs} index={1}>
              <Box display="flex" flexDirection="column" alignItems="center" textAlign="center" pt={2}>
                <Typography variant="h6" gutterBottom>
                  Modificar Icono Principal
                </Typography>
                
                <label htmlFor="icon-button-file">
                  <input
                    accept="image/png, image/jpeg"
                    style={{ display: 'none' }}
                    id="icon-button-file"
                    type="file"
                    onChange={handleImageChange}
                  />
                  <div className={image.previewUrl ? '' : classes.imageUploadBox}>
                    {image.previewUrl ? (
                      <img 
                        src={image.previewUrl} 
                        alt="Vista previa" 
                        style={{ 
                          maxWidth: '100%', 
                          height: 'auto',
                          maxHeight: 250, 
                          borderRadius: 8, 
                          cursor: 'pointer',
                          boxShadow: '0px 4px 10px rgba(0,0,0,0.1)'
                        }} 
                      />
                    ) : (
                      <Box display="flex" flexDirection="column" alignItems="center">
                        <PhotoCamera style={{ fontSize: 48, marginBottom: 8 }} />
                        <Typography variant="body2">Sin imagen</Typography>
                        <Typography variant="caption" color="textSecondary">(Clic para subir)</Typography>
                      </Box>
                    )}
                  </div>
                </label>
                
                {image.selectedBase64 && (
                  <Box mt={3}>
                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      onClick={handleSaveImage}
                    >
                      Guardar imagen
                    </Button>
                  </Box>
                )}
              </Box>
            </TabPanel>

            {/* TABS FUTURAS */}
            <TabPanel value={valueTabs} index={2}>
              <Box display="flex" justifyContent="center" p={4}>
                <Typography color="textSecondary">Próximamente...</Typography>
              </Box>
            </TabPanel>
            <TabPanel value={valueTabs} index={3}>
              <Box display="flex" justifyContent="center" p={4}>
                <Typography color="textSecondary">Próximamente...</Typography>
              </Box>
            </TabPanel>

          </div>
        </div>
      </form>
    </div>
  );
};

export default ConfigsTable;